import React from 'react';
import dayjs from 'dayjs';
import type { VoucherPrintData } from '@/services/salesInvoice.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export interface PrintableVoucherProps {
  voucher: VoucherPrintData;
  /** Meta row label for the document reference (e.g. Payment Voucher / Advance request). */
  referenceLabel?: string;
  companyName?: string;
  /** Print title override (default JOURNAL VOUCHER). */
  title?: string;
  /** Shown as Type meta when voucher.voucher_type is generic (e.g. Payment). */
  typeLabel?: string;
  onClose?: () => void;
}

const toNum = (v: any) => (v == null || v === '' ? 0 : Number(v));

function amt(v: unknown): string {
  const n = toNum(v);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Journal / accounting voucher — shared pagination engine with restored debit/credit detail. */
export const PrintableVoucher: React.FC<PrintableVoucherProps> = ({
  voucher,
  referenceLabel = 'Reference',
  companyName = '',
  title = 'JOURNAL VOUCHER',
  typeLabel,
  onClose,
}) => {
  const details = voucher.details ?? [];
  const voucherDate = voucher.voucher_date
    ? dayjs(voucher.voucher_date).format('DD-MM-YYYY')
    : '';
  const displayType = typeLabel || voucher.voucher_type || 'Journal';

  return (
    <EngineCommercialA4Print
      docType="journal_voucher"
      title={title}
      documentNumber={voucher.voucher_number || 'JV'}
      partyName={companyName || voucher.description || ''}
      onClose={onClose}
      introText={companyName || undefined}
      metaLines={[
        { label: 'Voucher No.', value: voucher.voucher_number || '' },
        { label: 'Date', value: voucherDate },
        { label: 'Type', value: displayType },
        { label: 'Status', value: voucher.status || '' },
        { label: referenceLabel, value: voucher.reference_number || '' },
        { label: 'Narration', value: voucher.description || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'code', label: 'Account Code' },
        { key: 'account', label: 'Account Name' },
        { key: 'description', label: 'Description' },
        { key: 'debit', label: 'Debit', align: 'right' },
        { key: 'credit', label: 'Credit', align: 'right' },
      ]}
      lines={details.map((line, idx) => {
        const desc = line.description ?? '';
        return {
          lineNumber: idx + 1,
          description: desc,
          cells: {
            code: line.account?.account_code ?? '—',
            account: line.account?.account_name ?? '—',
            description: desc,
            debit: toNum(line.debit_amount) > 0 ? amt(line.debit_amount) : '',
            credit: toNum(line.credit_amount) > 0 ? amt(line.credit_amount) : '',
          },
        };
      })}
      finals={[{ id: 'totals', heightPx: 48 }]}
      finalBlocks={{
        totals: (
          <div>
            <p>
              <strong>Total Debit:</strong> {amt(voucher.total_debit)} ·{' '}
              <strong>Total Credit:</strong> {amt(voucher.total_credit)}
            </p>
            <p style={{ fontSize: 11 }}>
              This voucher represents the accounting entries passed for the above reference.
            </p>
          </div>
        ),
      }}
    />
  );
};
